import React, { useEffect, useState, useRef } from "react";
import "react-toastify/dist/ReactToastify.css";
import axios from "utils/axios";
import "./Chatbot.css";
import Button from "components/ui/Button";
import { showToast } from "components/ui/toast";
import CustomQuillEditor from "components/ui/CustomQuillEditor";
import { FaPencil, FaTrash } from "react-icons/fa6";

const questions = [
  "What’s a moment in your life that changed everything for you?",
  "Did you ever have a turning point where you knew what you wanted to do with your life? What happened?",
  "Who or what inspires you the most, and why?",
  "What’s a lesson you’ve learned the hard way that stuck with you?",
  "Was there ever a time when you felt lost or unsure of your path? How did you find clarity?",
  "If you could go back and give your younger self one piece of advice, what would it be?",
  "What’s something you’ve overcome that shaped who you are today?",
  "Is there someone in your life who shaped the way you see the world? What’s the story behind that?",
  "What motivates you to get up every day and do what you do?",
  "When you think about 'success,' what does it actually look or feel like to you?",
  "What’s a memory that brings you joy or keeps you grounded when things get tough?",
  "What’s a fear you’ve faced that you didn’t think you could overcome?",
  "Is there a particular experience that makes you feel proud when you think about it?",
  "How do you want people to feel when they think of you or the work you do?",
];

const Chatbot = ({ data, onResultChange }) => {
  // Pick a random question
  const randomQuestion =
    questions[Math.floor(Math.random() * questions.length)];

  const [initialBotQuestion, setInitialBotQuestion] = useState("");

  const [messages, setMessages] = useState([
    { role: "system", content: data.initial || randomQuestion },
    ...(data.name === "vision"
      ? [
          {
            role: "system",
            content:
              "Here are some examples of how you could answer:\n\n" +
              "• I want to travel to ...\n" +
              "• I want to do X number of sales\n" +
              "• I want to have the greatest family life\n" +
              "• I want to be more grateful\n" +
              "• I want a car/boat/house etc.",
            isExample: true,
          },
        ]
      : []),
  ]);

  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);

  const [result, setResult] = useState(data?.result);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    resizeTextarea();
  }, [inputValue]);

  const chatgptAPICall = async (message, previousMessages) => {
    try {
      // Convert messages array to a readable string format
      const formattedMessages = previousMessages
        .map((msg) => `${msg.role}: ${msg.content}`)
        .join("\n");

      const response = await axios.post(`/openai/text`, {
        userMessage: `Previous conversation:
        ${formattedMessages}
        
        User's current message: ${message}
        
        Instructions: ${data.prompt1}`,
        systemPrompt: data.prompt1,
      });

      if (response.data.success) {
        return response.data.data;
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "An unexpected error occurred";
      console.log(errorMessage);
    }
  };

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  };

  const handleSend = async () => {
    if (!inputValue.trim()) {
      showToast("error", "Please type something");
      return;
    }

    setIsTyping(true);
    const userMessage = { role: "user", content: inputValue };
    setMessages([...messages, userMessage]);
    setInputValue("");

    try {
      const botResponseText = await chatgptAPICall(inputValue, messages);
      if (!botResponseText) {
        throw new Error("Error in response");
      }

      setIsTyping(false);
      const botResponse = { role: "system", content: "" };
      setMessages((prevMessages) => {
        const newMessages = [...prevMessages, botResponse];
        typewriterEffect(botResponseText, newMessages.length - 1);
        return newMessages;
      });
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "An unexpected error occurred";
      console.log(errorMessage);
      setIsTyping(false);
    }
  };

  useEffect(() => {
    const fetchInitialQuestion = async () => {
      if (!data.initial) {
        try {
          const botResponseText = await chatgptAPICall(
            "Generate first question to ask user",
            messages
          );
          if (!botResponseText) {
            throw new Error("Error in response");
          }

          setInitialBotQuestion(botResponseText); // Store the initial question
          const botResponse = { role: "system", content: botResponseText };
          setMessages((prevMessages) => {
            // Remove the empty initial message and add the new response
            const filteredMessages = prevMessages.filter(
              (msg) => msg.content !== ""
            );
            return [...filteredMessages, botResponse];
          });
        } catch (error) {
          const errorMessage =
            error.response?.data?.message ||
            error.message ||
            "An unexpected error occurred";
          console.log(errorMessage);
        }
      }
    };

    fetchInitialQuestion();
  }, [data.initial]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSend();
    }
  };

  const typewriterEffect = (text, index) => {
    let charIndex = -1;
    const interval = setInterval(() => {
      setMessages((prevMessages) => {
        const updatedMessages = [...prevMessages];
        if (updatedMessages[index]?.content?.length < text.length) {
          updatedMessages[index].content += text.charAt(charIndex);
        }
        return updatedMessages;
      });
      charIndex++;
      if (charIndex === text.length) {
        clearInterval(interval);
      }
    }, 20);
  };

  const resizeTextarea = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      const scrollHeight = textarea.scrollHeight;
      const lineHeight = parseInt(
        window.getComputedStyle(textarea).lineHeight,
        10
      );
      const rows = Math.floor(scrollHeight / lineHeight);
      const maxRows = 5;

      if (rows > maxRows) {
        textarea.style.height = `${lineHeight * maxRows}px`;
        textarea.style.overflowY = "auto";
      } else {
        textarea.style.height = `${scrollHeight}px`;
        textarea.style.overflowY = "hidden";
      }
    }
  };

  const generateStory = async () => {
    if (messages.length < 3) {
      showToast(
        "error",
        `Please have atleast few conversations to generate a better ${data?.name}`
      );
      return;
    }
    setLoading(true);

    try {
      const response = await axios.post(`/openai/text`, {
        userMessage: JSON.stringify(messages),
        systemPrompt: data.prompt2,
      });

      if (response.data.success) {
        updateResult(response.data.data);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "An unexpected error occurred";
      showToast("error", errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const updateResult = (newResult) => {
    setResult(newResult);
    if (onResultChange) {
      onResultChange(newResult); // Notify the parent component
    }
  };

  const handleReset = () => {
    const initialMessage = data.initial || randomQuestion || initialBotQuestion;

    setMessages([
      { role: "system", content: initialMessage },
      ...(data.name === "vision"
        ? [
            {
              role: "system",
              content:
                "Here are some examples of how you could answer:\n\n" +
                "• I want to travel to ...\n" +
                "• I want to do X number of sales\n" +
                "• I want to have the greatest family life\n" +
                "• I want to be more grateful\n" +
                "• I want a car/boat/house etc.",
              isExample: true,
            },
          ]
        : []),
    ]);
  };

  const [descriptionEdit, setDescriptionEdit] = useState(false);
  const [tempDescription, setTempDescription] = useState(result);

  const handleSave = () => {
    updateResult(tempDescription);
    setDescriptionEdit(false);
  };

  const handleCancel = () => {
    setTempDescription(result);
    setDescriptionEdit(false);
  };

  if (result) {
    return (
      <div
        id="description"
        className="space-y-4 w-full flex flex-col items-center justify-center"
      >
        <div className="w-full flex items-center justify-between gap-4 relative">
          <Button
            className="outline-button"
            onClick={() => {
              setResult(null);
              handleReset(); // Invoke the function
            }}
            disabled={loading || isTyping}
          >
            Start again
          </Button>
          {!descriptionEdit && (
            <div
              className="ml-auto flex items-center gap-1.5 cursor-pointer"
              onClick={() => {
                setTempDescription(result);
                setDescriptionEdit(true);
              }}
            >
              <FaPencil className="w-3 h-3" /> <span>Edit</span>
            </div>
          )}
        </div>

        {!descriptionEdit && (
          <div
            dangerouslySetInnerHTML={{ __html: result }}
            className="space-y-4"
          ></div>
        )}

        {descriptionEdit && (
          <CustomQuillEditor
            value={tempDescription}
            setValue={setTempDescription}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        )}
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col justify-between">
      <div className="w-full max-w-4xl mx-auto flex flex-col flex-grow items-center">
        <div
          id="msg"
          ref={messagesContainerRef}
          className="w-full enhanced-textarea overflow-y-auto pb-8"
        >
          {messages.map((message, index) => (
            <div
              key={index}
              className={`mb-4 ${
                message.role === "system" ? "text-left" : "text-right"
              }`}
            >
              <span
                className={`inline-block p-2 max-w-[80%] rounded-lg ${
                  message.role === "system"
                    ? message.isExample // Check if it's the example message
                      ? "text-darkgray rounded-br-none"
                      : "bg-white rounded-br-none"
                    : "text-start bg-lightgray rounded-bl-none"
                }`}
              >
                <pre
                  className={`${
                    message.isExample ? "text-[12px]" : "text-[16px]"
                  } `}
                >
                  {message.content}
                </pre>
              </span>
            </div>
          ))}

          {isTyping && (
            <div className="text-left mb-2">
              <span className="inline-block p-2 max-w-[80%] bg-lightgray rounded-lg animate-pulse">
                Typing...
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-lg lg:mx-auto fixed lg:bottom-20 bottom-10 left-0 right-0 lg:w-full w-auto mx-11 bg-white px-2 py-1">
        <div className="mt-1 flex gap-2 justify-center">
          <Button
            className="outline-button"
            onClick={handleReset}
            disabled={loading || isTyping}
          >
            Start again
          </Button>
          <Button
            className="black-button"
            onClick={generateStory}
            disabled={loading || isTyping}
            loading={loading}
          >
            I'm done, write my {data.name}
          </Button>
        </div>
        <div className="pb-2 flex gap-2 items-end justify-center p-2 border-t border-lightgray">
          <textarea
            ref={textareaRef}
            value={inputValue}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder="Type here..."
            className="flex-grow p-2 bg-white border border-mediumgray rounded-md outline-none focus:outline-none resize-none overflow-y-hidden min-h-[32px]"
            rows="1"
          />
          <Button onClick={handleSend} className="black-button rounded-md">
            <i className="fa-solid fa-location-arrow"></i>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Chatbot;
